import { SequenceFlowState, SequenceFlow, FlowElement, ExclusiveGateway, Expression, FlowNode, InclusiveGateway } from "@edp-pmf/bpmn-model";
import { SequenceFlowEdge, FarrisPropConfigWithData, ElementPropertyConfig } from "@edp-pmf/grapheditor";
import { UserActivity, Condition } from "@gsp-wf/wf-process-model";
import { SequenceFlowPropertyKeys } from "./property-keys/SequenceFlowPropertyKeys";
import { WfLocalePipe } from "../../i18n/pipe/wf-process-editor-locale.pipe";
import { mxResources } from "@edp-pmf/mxgraph-ts";

/**
 * 事件流连线，增加状态颜色信息
 */
export class WfSequenceFlowEdge extends SequenceFlowEdge {
    localePipe: WfLocalePipe = null;
    strokeColors = {
        [SequenceFlowState.Default]: "#6482b9",
    };

    getFarrisPropConfig(): FarrisPropConfigWithData {
        const sequenceFlow = this.flowElement;
        const sourceNode = sequenceFlow.Model.DefaultProcess.GetFlowElement(sequenceFlow.SourceRef);
        let conditionConfig: ElementPropertyConfig = new ElementPropertyConfig();
        let basicConfig: ElementPropertyConfig = new ElementPropertyConfig();
        let propertyData = {};

        basicConfig.categoryId = 'basic';
        basicConfig.categoryName =  mxResources.get('basicInfo');
        basicConfig.tabId = 'basic';
        basicConfig.tabName =  mxResources.get('basic')
        basicConfig.properties = [
            { propertyID: SequenceFlowPropertyKeys.Name, propertyName:  mxResources.get('name'), propertyType: 'string', visible: !this.editorUi.multiLangEnabled },
            { propertyID: SequenceFlowPropertyKeys.NameLanguage, propertyName:  mxResources.get('nameLanguage'), propertyType: 'multiLanguage', visible: this.editorUi.multiLangEnabled },
            { propertyID: SequenceFlowPropertyKeys.Id, propertyName:  mxResources.get('id'), propertyType: 'string', readonly: true }
        ];
        conditionConfig.categoryId = 'condition';
        conditionConfig.categoryName =  mxResources.get('condition');
        conditionConfig.tabId = 'basic';
        conditionConfig.tabName =  mxResources.get('basic');
        if(sourceNode.ClrTypeID !== 'bpmn.ParallelGateway') {
            conditionConfig.properties = [
                { propertyID: SequenceFlowPropertyKeys.ConditionExpression, propertyName:  mxResources.get('ConditionExpression'), propertyType: 'modal' }
            ];
        }
        // 源节点为排他网关和UserActivity时
        if (sourceNode.ClrTypeID === UserActivity.Clr_Type_ID || sourceNode.ClrTypeID === 'bpmn.ExclusiveGateway' || sourceNode.ClrTypeID === 'bpmn.InclusiveGateway') {
            conditionConfig.properties.push(
                { propertyID: SequenceFlowPropertyKeys.Default, propertyName:  mxResources.get('default'), propertyType: 'select' }
            );
        }

        this.setSequenceFlowValue(sequenceFlow, sourceNode, propertyData);
        return {
            propertyConfig: [basicConfig, conditionConfig],
            propertyData: propertyData
        };
    }

    updateProps(obj: any): void {
        const sequenceFlow = this.flowElement;
        let propertyId: string = obj.propertyID;
        switch (propertyId) {
            case SequenceFlowPropertyKeys.ConditionExpression:                                         // 更新条件
                this.updateConditionExpr(sequenceFlow, obj.propertyValue);
                break;
            case SequenceFlowPropertyKeys.Default:
                this.modifyDefaultSequenceFlow(sequenceFlow, obj.propertyValue);
                break;
            default:
                super.updateProps(obj);
                break;
        }
    }

    /**
     * 设置连接线属性值
     * @param sequence
     * @param propertyData
     */
    setSequenceFlowValue(sequenceFlow: SequenceFlow, sourceNode: FlowElement, propertyData: Object) {
        propertyData[SequenceFlowPropertyKeys.Id] = sequenceFlow.Id;
        propertyData[SequenceFlowPropertyKeys.ClrTypeID] = sequenceFlow.ClrTypeID;
        propertyData[SequenceFlowPropertyKeys.Name] = sequenceFlow.Name;
        propertyData[SequenceFlowPropertyKeys.NameLanguage] = sequenceFlow.NameLanguage || {
            "zh-CHS": sequenceFlow.Name,
        };
        if (!sequenceFlow.ConditionExpression || !sequenceFlow.ConditionExpression.MexExpression)
            propertyData[SequenceFlowPropertyKeys.ConditionExpression] = '';
        else
            propertyData[SequenceFlowPropertyKeys.ConditionExpression] =
                (sequenceFlow.ConditionExpression.MexExpression as Condition).Value;

        if (sourceNode.ClrTypeID === UserActivity.Clr_Type_ID) {
            const userActivity = sourceNode as UserActivity;
            if (userActivity.Default === sequenceFlow.Id) {
                propertyData[SequenceFlowPropertyKeys.Default] = true;
            } else {
                propertyData[SequenceFlowPropertyKeys.Default] = false;
            }
        }
        if (sourceNode.ClrTypeID === 'bpmn.ExclusiveGateway') {
            const gateway = sourceNode as ExclusiveGateway;
            if (gateway.Default === sequenceFlow.Id) {
                propertyData[SequenceFlowPropertyKeys.Default] = true;
            } else {
                propertyData[SequenceFlowPropertyKeys.Default] = false;
            }
        }
        if (sourceNode.ClrTypeID === 'bpmn.InclusiveGateway') {
            const gateway = sourceNode as InclusiveGateway;
            if (gateway.Default === sequenceFlow.Id) {
                propertyData[SequenceFlowPropertyKeys.Default] = true;
            } else {
                propertyData[SequenceFlowPropertyKeys.Default] = false;
            }
        }
    }

    /**
     * 更新分支条件
     * @param seqFlow
     * @param val
     */
    updateConditionExpr(seqFlow: SequenceFlow, val: string) {
        seqFlow.ConditionExpression = null;
        if (val) {
            let expression = new Expression(seqFlow.Model);
            let condition = new Condition(seqFlow.Model);
            condition.Value = val;
            expression.MexExpression = condition;
            seqFlow.ConditionExpression = expression;
        }
    }

    /**
     * 更新默认分支(当前适用：排他网关+活动节点)
     * @param seqFlow
     * @param val
     */
    modifyDefaultSequenceFlow(seqFlow: SequenceFlow, val: boolean) {
        // 获取流转线源节点
        const node = seqFlow.Model.DefaultProcess.GetFlowElement(seqFlow.SourceRef) as FlowNode;
        if (node.ClrTypeID === UserActivity.Clr_Type_ID) {
            // 节点为UserActivity类型
            const userActivity = node as UserActivity;
            if (val === true) {
                userActivity.Default = seqFlow.Id;
            } else if (val === false) {
                // 源节点的默认分支===流转分支ID
                if (userActivity.Default === seqFlow.Id) {
                    userActivity.Default = null;
                }
            }
        } else if (node.ClrTypeID === 'bpmn.ExclusiveGateway') {
            // 排他网关
            const gateway = node as ExclusiveGateway;
            if (val === true) {
                gateway.Default = seqFlow.Id;
            } else if (val === false) {
                // 源节点的默认分支===流转分支ID
                if (gateway.Default === seqFlow.Id) {
                    gateway.Default = null;
                }
            }
        } else if (node.ClrTypeID === 'bpmn.InclusiveGateway') {
            // 包容网关
            const gateway = node as InclusiveGateway;
            if (val === true) {
                gateway.Default = seqFlow.Id;
            } else if (val === false) {
                // 源节点的默认分支===流转分支ID
                if (gateway.Default === seqFlow.Id) {
                    gateway.Default = null;
                }
            }
        }
    }
}
